<script lang="ts">
  import Icon, { isIconVariant } from '$components/ui/icon/icon.svelte';
  import type {
    GetProductQuery,
    ProductCardFragment,
    QuickAddToCartFragment
  } from '$gql/storefront/operations';
  import { cn } from '$lib/utils';
  import { formatCurrency } from '$lib/utils/currency';
  import type { SubscriptionPlanStoryblok } from '$types/bloks';

  export let product:
    | NonNullable<GetProductQuery['product']>
    | ProductCardFragment
    | QuickAddToCartFragment;
  export let selectedPlanId: string | undefined;
  export let selectedProductVariant:
    | NonNullable<GetProductQuery['product']>['variants']['nodes'][number]
    | undefined = undefined;
  export let subscriptionPlansExtendedInfo: SubscriptionPlanStoryblok[];

  $: sellingPlans =
    product.sellingPlanGroups.edges.length > 0
      ? product.sellingPlanGroups.edges[0]?.node.sellingPlans.edges
          .map((e) => e.node)
          .map((plan) => {
            const variantPlan = selectedProductVariant?.sellingPlanAllocations?.edges.find(
              (allocation) => allocation.node.sellingPlan.id === plan.id
            )?.node;

            const infoPlan = subscriptionPlansExtendedInfo.find(
              (info) => info.plan_name === plan.name
            );

            return {
              id: plan.id,
              name: plan.name || '',
              price: variantPlan?.priceAdjustments[0].price.amount,
              currency: variantPlan?.priceAdjustments[0].price.currencyCode,
              title: infoPlan?.title,
              infos: infoPlan?.infos
            };
          })
      : [];

  const sellingPlanName =
    product.sellingPlanGroups.edges.length > 0 ? product.sellingPlanGroups.edges[0]?.node.name : '';

  $: selectedPlan = sellingPlans.find((plan) => plan.id === selectedPlanId);
</script>

{#if selectedProductVariant}
  <div class="mb-4">
    <div class={cn('flex gap-2 mb-2 text-sm font-medium text-primary')}>
      <p>{sellingPlanName}</p>
    </div>

    <form class="flex flex-wrap gap-2 h-fit">
      {#each sellingPlans as plan}
        <div class={cn('grid place-items-center')}>
          <input
            type="radio"
            value={plan.id}
            id={plan.id}
            name="plan"
            bind:group={selectedPlanId}
            class={cn('invisible visually-hidden [&:focus-visible+label]:ring')}
          />

          <label
            for={plan.id}
            class={cn(
              'cursor-pointer rounded-xs transition-colors',
              selectedPlanId === plan.id
                ? 'bg-accent text-highlight'
                : 'bg-primary/4 hover:bg-primary/10'
            )}
          >
            <p class="text-[11px] py-[5px] text-center">{plan.title}</p>
            <div
              class={cn(
                'bg-highlight text-primary border rounded-xs py-2.5 px-3.5',
                selectedPlanId === plan.id ? 'border-accent' : 'border-primary/4'
              )}
            >
              <p class="text-sm text-center mb-0.5">
                {plan.name}
              </p>
              {#if plan.price && plan.currency}
                <p class="text-xs text-center opacity-50">
                  {`${$formatCurrency(plan.currency, plan.price)} /month`}
                </p>
              {/if}
            </div>
          </label>
        </div>
      {/each}
    </form>

    {#if selectedPlan?.infos}
      <div class="flex flex-col gap-2.5 mt-2">
        {#each selectedPlan.infos as info}
          {@const icon = info.icon?.toString()}
          <p class="text-sm text-primary/60 flex items-center gap-x-1">
            {#if icon && isIconVariant(icon)}
              <Icon {icon} size="md" />
            {/if}
            <span class={cn('block text-sm')}>{info.label}</span>
          </p>
        {/each}
      </div>
    {/if}
  </div>
{/if}
